// orientationLockBus.ts
import * as SO from "expo-screen-orientation";

export type LockEvent = {
  ts: number;
  lock: SO.OrientationLock | null;
  method: "lockAsync" | "unlockAsync" | "setOrientationLockAsync" | "verify";
  origin?: string; // parsed from the stack (file:line:col)
  stack?: string; // full stack (optional, for debugging)
};

type LockListener = (lock: SO.OrientationLock | null) => void;
type EventListener = (e: LockEvent) => void;

let patched = false;
let current: SO.OrientationLock | null = null;
let lastEvent: LockEvent | null = null;

const lockListeners = new Set<LockListener>();
const eventListeners = new Set<EventListener>();

export function emitEvent(partial: Omit<LockEvent, "ts">) {
  const ev: LockEvent = { ts: Date.now(), ...partial };
  lastEvent = ev;
  current = ev.lock;
  // Back-compat: notify simple lock listeners
  lockListeners.forEach((fn) => fn(ev.lock));
  // Full event listeners
  eventListeners.forEach((fn) => fn(ev));
}

function basename(p: string) {
  return p.split(/[\\/]/).pop()!;
}

function parseOrigin(stack?: string): string | undefined {
  if (!stack) return;

  // Make sure we have enough frames to search (dev only)
  if (__DEV__ && (Error as any).stackTraceLimit != null) {
    (Error as any).stackTraceLimit = Math.max(
      (Error as any).stackTraceLimit,
      50
    );
  }

  const lines = stack
    .split("\n")
    .slice(1)
    .map((l) => l.trim());

  // 1) Remove noisy frames (bundles, RN internals, our bus, expo lib, etc.)
  const interesting = lines.filter(
    (l) =>
      !/orientationLockBus|expo-screen-orientation|react-native\/Libraries|node_modules|index\.bundle|hermes-stable|native code/i.test(
        l
      )
  );

  // 2) Prefer "at fn (path:line:col)" with real source extensions
  for (const l of interesting) {
    const m = l.match(/at\s+(.+?)\s+\(([^)]+?\.(?:tsx?|jsx?)):(\d+):(\d+)\)/);
    if (m) return `${basename(m[2])}:${m[3]} in ${m[1]}`;
  }

  // 3) Fallback: bare "path:line:col" with real source extensions
  for (const l of interesting) {
    const m = l.match(/([^()\s]+?\.(?:tsx?|jsx?)):(\d+):(\d+)/);
    if (m) return `${basename(m[1])}:${m[2]}`;
  }

  // 4) Last resort: first interesting line (still better than bundle URL)
  return interesting[0];
}
function verifySoon() {
  setTimeout(async () => {
    try {
      const v = await SO.getOrientationLockAsync();
      if (v !== current) {
        emitEvent({
          lock: v,
          method: "verify",
          origin: "verify",
          stack: undefined,
        });
      }
    } catch {}
  }, 0);
}

export function ensureOrientationLockPatched() {
  if (patched) return;
  patched = true;

  const origLock = SO.lockAsync.bind(SO);
  const origUnlock = SO.unlockAsync.bind(SO);

  (SO as any).lockAsync = async (next: SO.OrientationLock) => {
    const stack = new Error().stack;
    const res = await origLock(next);
    emitEvent({
      lock: `${next} y`,
      method: "lockAsync",
      origin: parseOrigin(stack),
      stack,
    });
    verifySoon();
    return res;
  };

  (SO as any).unlockAsync = async () => {
    const stack = new Error().stack;
    const res = await origUnlock();
    emitEvent({
      lock: `${SO.OrientationLock.DEFAULT} x`,
      method: "unlockAsync",
      origin: parseOrigin(stack),
      stack,
    });
    verifySoon();
    return res;
  };

  // Prime initial value
  verifySoon();

  // Debug: ensure single instance
  (globalThis as any).__SO_BUS_ID ??= Math.random().toString(36).slice(2);
  emitEvent({ note: "bus id initialized: " + (globalThis as any).__SO_BUS_ID });
  console.log("[SO] bus id:", (globalThis as any).__SO_BUS_ID);
}

export function addLockListener(fn: LockListener) {
  lockListeners.add(fn);
  if (current != null) fn(current);
  return () => lockListeners.delete(fn);
}

export function addLockEventListener(fn: EventListener) {
  eventListeners.add(fn);
  if (lastEvent) fn(lastEvent);
  return () => eventListeners.delete(fn);
}
